// Parity P1 wave 1: the task's description, edited where it is read (hybrid design §3.1
// "Description inline (BlockNote, slash menu, image paste/drop → task Files), fold at 8 lines,
// optimistic revision writes with `If-Match`; a remote update while editing shows 'Updated by
// Riley · Reload / Keep mine (new version)'; the local draft survives task switches and
// navigation until saved").
//
// The description is the task's body page (M7-02). Each change autosaves as a new page version
// under `If-Match`: the version this editor started from. A refusal (409: someone saved in
// between), or a newer version arriving while there are unsaved changes, raises the banner;
// "Keep mine" saves the draft over the newest version (a new version, nothing lost: the other
// one stays in the page's history), "Reload" drops the draft. The draft lives in localStorage
// per task until it is saved, so closing the drawer or opening another task keeps it.
import { ApiError } from "@companyos/sdk";
import { Button, Notice, type PageDocument, PageEditor, Skeleton } from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ChevronDownIcon, ChevronUpIcon, FileTextIcon, PlusIcon } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { type Api, apiUrl } from "../../../api/client";
import { keepFocus } from "../../../focus/focus";
import type { CompanyAccess } from "../../company";
import type { SeatPeople } from "../model";
import type { TaskDetail } from "../view-engine";
import { invalidateWork, pageQuery, workKeys } from "../work-queries";

/** `rev`: the edit count these blocks include (this editor's session; 0 for a stored draft). */
type Draft = { base: number; blocks: unknown[]; markdown: string; at: number; rev?: number };

const draftKey = (co: string, task: string) => `cos:task-description:${co}:${task}`;

/** The saved draft of a task's description, or null (storage may be unavailable). */
export function readDraft(co: string, task: string): Draft | null {
  try {
    const raw = localStorage.getItem(draftKey(co, task));
    return raw ? (JSON.parse(raw) as Draft) : null;
  } catch {
    return null;
  }
}

function writeDraft(co: string, task: string, d: Draft | null) {
  try {
    if (d) localStorage.setItem(draftKey(co, task), JSON.stringify(d));
    else localStorage.removeItem(draftKey(co, task));
  } catch {
    // Private mode or full storage: the draft lives only as long as the editor.
  }
}

/** Folded height: about eight lines of body text. */
const FOLD_PX = 8 * 24;

export function TaskDescription({
  api,
  company,
  task,
  people,
  canEdit,
}: {
  api: Api;
  company: CompanyAccess;
  task: TaskDetail;
  people: SeatPeople;
  canEdit: boolean;
}) {
  const co = company.id;
  const qc = useQueryClient();
  const add = useMutation({
    mutationFn: () => api.client.tasks.page({ params: { co, id: task.id } }),
    onSuccess: (p) => {
      qc.setQueryData(workKeys.page(co, p.id), p);
      void invalidateWork(qc, co);
    },
  });
  if (!task.page_id)
    return (
      <section
        aria-label="Description"
        data-slot="task-description"
        className="flex items-center gap-2"
      >
        <FileTextIcon aria-hidden="true" className="size-4 shrink-0 text-faint" />
        <span className="text-sm text-muted-foreground">No description yet.</span>
        {canEdit ? (
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto gap-1.5"
            disabled={add.isPending}
            onClick={() => add.mutate()}
          >
            <PlusIcon aria-hidden="true" />
            Add a description
          </Button>
        ) : null}
      </section>
    );
  return (
    <DescriptionEditor
      key={task.page_id}
      api={api}
      co={co}
      companySlug={company.slug}
      taskId={task.id}
      pageId={task.page_id}
      title={task.title}
      people={people}
      canEdit={canEdit}
    />
  );
}

function DescriptionEditor({
  api,
  co,
  companySlug,
  taskId,
  pageId,
  title,
  people,
  canEdit,
}: {
  api: Api;
  co: string;
  companySlug: string;
  taskId: string;
  pageId: string;
  title: string;
  people: SeatPeople;
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const page = useQuery(pageQuery(api, co, pageId));
  // The version the editor's content started from (If-Match), the unsaved draft, and a nonce
  // that remounts the editor on Reload.
  const base = useRef<number | null>(null);
  // A stored draft holds none of this editor session's edits: its revision restarts at 0.
  const [draft, setDraft] = useState<Draft | null>(() => {
    const d = readDraft(co, taskId);
    return d ? { ...d, rev: 0 } : null;
  });
  const [conflict, setConflict] = useState<{ version: number; by: string } | null>(null);
  const [nonce, setNonce] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const [focused, setFocused] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  const editable = canEdit && (page.data?.can_edit ?? false);
  const byName = useCallback(
    (m: string | null | undefined) => (m ? people.nameOf(m).name : "someone"),
    [people],
  );

  // First load: start from the draft's base when there is a draft, else the page's version.
  if (page.data && base.current === null) base.current = draft?.base ?? page.data.version;

  // The unsaved draft, held in memory (localStorage only persists it: it may be unavailable).
  const latest = useRef<Draft | null>(draft);
  const keep = (d: Draft | null) => {
    latest.current = d;
    writeDraft(co, taskId, d);
    setDraft(d);
  };
  // Edits counts input as it happens (beforeinput, paste, drop), ahead of the editor's
  // debounced change report; `clean` is the count the last successful save covered. While they
  // differ the editor holds work that is not saved, so a newer version arriving is a conflict,
  // never a silent replacement.
  const edits = useRef(0);
  const clean = useRef(0);
  const dirty = () => latest.current !== null || edits.current !== clean.current;
  const touch = () => {
    if (editable) edits.current += 1;
  };
  // The editor's own change callback (typing, toolbar, menus, block moves), synchronous: only
  // the current editor instance counts.
  const touchOf = (gen: number) => () => {
    if (gen === generation.current) touch();
  };
  // The editor instance whose changes count: a remount (Reload, a remote version) ignores the
  // old instance's last flush.
  const generation = useRef(0);
  generation.current = nonce;
  const conflictRef = useRef(conflict);
  conflictRef.current = conflict;
  const inFlight = useRef(false);

  // A newer version arrived (another tab, an agent): with unsaved or unreported changes it is a
  // conflict; without, the editor takes it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `dirty` reads refs; `draft` re-runs it.
  useEffect(() => {
    const p = page.data;
    if (!p || base.current === null || p.version <= base.current) return;
    if (dirty()) {
      setConflict({ version: p.version, by: byName(p.actor_membership_id) });
    } else {
      base.current = p.version;
      setNonce((n) => n + 1);
    }
  }, [page.data, draft, byName]);

  type Save = { blocks: unknown[]; ifMatch: number; rev: number };
  const save = useMutation({
    mutationFn: (v: Save) =>
      api.client.pages.update({
        params: { co, id: pageId },
        ifMatch: v.ifMatch,
        body: { body: v.blocks as never },
      }),
    onMutate: () => {
      inFlight.current = true;
    },
    onSuccess: (p, v) => {
      inFlight.current = false;
      base.current = p.version;
      qc.setQueryData(workKeys.page(co, pageId), p);
      setConflict(null);
      conflictRef.current = null;
      const now = latest.current;
      if (now && JSON.stringify(now.blocks) !== JSON.stringify(v.blocks)) {
        // Reported while this save was out: the newer draft builds on this version; save it too.
        const rebased = { ...now, base: p.version };
        keep(rebased);
        send({ blocks: rebased.blocks, ifMatch: p.version, rev: rebased.rev ?? 0 });
        return;
      }
      keep(null);
      // Input typed after the saved report but not reported yet keeps the editor dirty; its
      // report (the debounce) saves it.
      clean.current = v.rev;
    },
    onError: async (e) => {
      inFlight.current = false;
      if (e instanceof ApiError && e.status === 409) {
        const newest = await qc.fetchQuery({ ...pageQuery(api, co, pageId), staleTime: 0 });
        setConflict({ version: newest.version, by: byName(newest.actor_membership_id) });
      }
    },
  });
  const send = (v: Save) => {
    inFlight.current = true;
    save.mutate(v);
  };

  const onChangeOf = (gen: number) => (doc: PageDocument) => {
    if (gen !== generation.current) return;
    const d: Draft = {
      base: base.current ?? page.data?.version ?? 1,
      blocks: doc.blocks,
      markdown: doc.markdown,
      at: Date.now(),
      // The report reads the document as it is now: it includes every edit counted so far.
      rev: edits.current,
    };
    keep(d);
    // A save in flight picks the newer draft up when it answers (onSuccess).
    if (!conflictRef.current && !inFlight.current)
      send({ blocks: d.blocks, ifMatch: d.base, rev: d.rev ?? 0 });
  };

  const keepMine = () => {
    const d = latest.current;
    if (!d || !conflict) return;
    send({ blocks: d.blocks, ifMatch: conflict.version, rev: d.rev ?? 0 });
  };
  const reload = () => {
    keep(null);
    clean.current = edits.current;
    setConflict(null);
    base.current = page.data?.version ?? null;
    void qc.invalidateQueries({ queryKey: workKeys.page(co, pageId) });
    setNonce((n) => n + 1);
  };

  // Fold long descriptions while not being edited.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure when the content changes.
  useLayoutEffect(() => {
    const el = box.current;
    if (el) setOverflows(el.scrollHeight > FOLD_PX + 8);
  }, [page.data?.version, nonce, draft?.at]);

  if (page.isPending) return <Skeleton className="h-16 w-full" />;
  if (page.isError)
    return <p className="text-sm text-muted-foreground">The description could not be loaded.</p>;

  const folded = overflows && !expanded && !focused;
  return (
    <section aria-label="Description" data-slot="task-description" className="flex flex-col gap-2">
      {conflict ? (
        <Notice tone="warning" data-slot="description-conflict">
          <span className="flex flex-wrap items-center gap-2">
            <span className="mr-auto">Updated by {conflict.by} while you were typing.</span>
            <Button size="sm" variant="ghost" onClick={reload}>
              Reload
            </Button>
            <Button size="sm" onClick={keepMine} disabled={save.isPending}>
              Keep mine (new version)
            </Button>
          </span>
        </Notice>
      ) : null}
      <div
        ref={box}
        onFocusCapture={() => setFocused(true)}
        onBeforeInputCapture={touch}
        onPasteCapture={touch}
        onDropCapture={touch}
        onBlurCapture={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocused(false);
        }}
        // max-h-48: FOLD_PX (eight 24 px lines).
        className={folded ? "relative max-h-48 overflow-hidden" : undefined}
      >
        <PageEditor
          key={`${pageId}:${nonce}`}
          label={`${title}: description`}
          width="full"
          initialBlocks={draft?.blocks ?? (page.data.body as unknown[])}
          editable={editable}
          placeholder="Describe the work, or press / for blocks…"
          onChange={editable ? onChangeOf(nonce) : undefined}
          onEdit={editable ? touchOf(nonce) : undefined}
          uploadFile={editable ? (file) => uploadToTask(api, co, taskId, file) : undefined}
        />
        {folded ? (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-card" />
        ) : null}
      </div>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        {overflows && !focused ? (
          <Button
            variant="ghost"
            size="xs"
            className="-ml-2"
            aria-expanded={expanded}
            onClick={() => setExpanded((x) => !x)}
          >
            {expanded ? (
              <ChevronUpIcon aria-hidden="true" />
            ) : (
              <ChevronDownIcon aria-hidden="true" />
            )}
            {expanded ? "Show less" : "Show more"}
          </Button>
        ) : null}
        <span data-slot="description-state" className="ml-auto">
          {save.isPending
            ? "Saving…"
            : draft && !conflict
              ? "Unsaved draft"
              : page.data.version > 1
                ? `Version ${page.data.version}`
                : null}
        </span>
        {/* M7-02: the body is a page; its page has the history and the full editor. */}
        <Link
          to="/c/$slug/work/p/$pageId"
          params={{ slug: companySlug, pageId }}
          search={keepFocus}
          className="inline-flex items-center gap-1 hover:text-foreground hover:underline"
        >
          <FileTextIcon aria-hidden="true" className="size-3.5" />
          Open page
        </Link>
      </div>
    </section>
  );
}

/**
 * An image pasted or dropped into the description lands in the task's Files (design §3.1) and
 * the editor embeds its content URL (the session cookie reads it). The in-memory API has no URL
 * to serve, so there the image is embedded as data.
 */
async function uploadToTask(api: Api, co: string, taskId: string, file: File): Promise<string> {
  const form = new FormData();
  form.set("file", file, file.name || "pasted image");
  form.set("task_id", taskId);
  const f = await api.client.files.upload({ params: { co }, body: form });
  if (api.baseUrl) return apiUrl(api.baseUrl, `/v1/companies/${co}/files/${f.id}/content`);
  return await new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}
